import type { PropsWithChildren } from "react";
import { Figure } from "@/components/bloks/Media/components/Figure";
import type { AssetStoryblok } from "@/generated/sb/sb-types";

export interface GiftCardSplitLayoutProps {
	image?: AssetStoryblok | null;
}

/**
 * Image-left / content-right layout, shared by every step of the gift card
 * flow: the `giftcard` block (step one) and the `/giftcard/address` and
 * `/giftcard/payment` routes, which persist the block's image via
 * `useGiftCardImage` since they aren't rendered through Storyblok themselves.
 *
 * Sizing matches `ProductDetailsPage`'s image/content split exactly — same
 * 12-column grid, same `lg` breakpoint, same column spans (`[1/8]` /
 * `[9/13]`) and gap token — rather than an independent layout for this flow.
 *
 * Without an image, renders `children` as-is — no grid, single column.
 *
 * The content side is wrapped in one div deliberately: `children` here is
 * typically a tree of context providers with no DOM element of their own
 * (GiftCardProviders, CheckoutProvider, AdyenProvider…), so without this
 * wrapper the grid would treat each of the underlying page's top-level
 * elements as a separate grid item and scatter them.
 */
export const GiftCardSplitLayout = ({
	image,
	children,
}: PropsWithChildren<GiftCardSplitLayoutProps>) => {
	if (!image?.filename) return <>{children}</>;

	return (
		<div className="grid grid-cols-1 gap-(--layout-gap) lg:grid-cols-12">
			<div className="col-span-full lg:col-[1/8]">
				{/*
				 * `aspect-3/4` crops whatever aspect ratio the marketing image was
				 * uploaded at into a portrait box. `object-cover` on a landscape
				 * source in a portrait box scales off the *height*, which can need
				 * ~2.7x more source width than the column's own CSS width — a plain
				 * vw-based `sizes` can't express that (100vw wasn't enough; still
				 * upscaled 640→885 tall). This is one hero image, not a repeated
				 * grid, so skip the viewport-width approximation entirely and
				 * request the largest tier outright — Next's optimizer caps at the
				 * source's own resolution, so this can't ask for more than the
				 * uploaded 2880px actually has.
				 */}
				<Figure
					asset={image}
					containerClassName="w-full"
					mediaClassName="aspect-3/4"
					sizes="3840px"
				/>
			</div>
			<div className="col-span-full w-full lg:col-[9/13] lg:self-start">{children}</div>
		</div>
	);
};
